import type { ObjectId } from 'mongodb';

import type { ContractSetting, OrganizationContractSettingsDocument } from './OrganizationSettings';

export enum ContractDocumentProviders {
  docusign = 'docusign',
  illinoisShines = 'illinoisShines',
  conga = 'conga',
}

export enum AdditionalSignerRole {
  salesRep = 'salesRep',
  epcInstallerRep = 'epcInstallerRep',
}

export enum ContractDocumentStatus {
  created = 'created',
  declined = 'declined',
  partiallySigned = 'partiallySigned',
  sent = 'sent',
  signed = 'signed',
  voided = 'voided',
}

export interface ContractDocumentSettingsFilter {
  enabled: boolean;
  type: string;
  value: string;
}

export interface ContractDocumentTemplates {
  templateId: string;
  language: string;
  coborrower: boolean;
  enabled: boolean;
}

export interface ContractDocumentSettings {
  id: ObjectId;
  enabled: boolean;
  name: string;
  isEPCContract?: boolean;
  versionId: string;
  versionNumber: number;
  type: string;
  provider: ContractDocumentProviders;
  providerData: {
    /** @warning Careful with this, it's overridden by the one from the templates array. */
    templateId?: string | null;
  };
  templateData: Record<string, any>;
  filters: Array<ContractDocumentSettingsFilter>;
  templates?: Array<ContractDocumentTemplates>;
  /** Applies to every provider. The Utility Account Holder is intentionally not a role. */
  requiredAdditionalSigners?: AdditionalSignerRole[];
  /** Conga only. Puerto Rico rows are always bilingual. */
  congaSpanishAvailable?: boolean;
  /** Conga only. Sent to Conga, which performs the eVault deposit. */
  vaultOnCompletion?: boolean;
  /** Conga only. Required by eOriginal when vaulting. */
  eOriginalLender?: string | null;
}

export interface JoinedContractDocumentSetting extends ContractSetting {
  contractDocumentSettings: ContractDocumentSettings;
}

/** This a join of the org settings' contractSettings type and matched ContractDocumentSettings docs */
export interface OrganizationContractDocumentSettings extends Omit<
  OrganizationContractSettingsDocument,
  'contractSettings' | 'id'
> {
  key: string;
  organizationSettingsId: ObjectId;
  contractSettings: JoinedContractDocumentSetting;
}
